import SiteCoreLinks from "@/components/SiteCoreLinks";
// /police-clearance-foreigner — TH Hub for foreigners in Thailand
import { createFileRoute, Link } from "@tanstack/react-router";
import { FOREIGNER_NATIONALITIES } from "@/data/police-foreigner-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { ShieldCheck, Globe2, MessageCircle, Phone, MapPin, BadgeCheck } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/police-clearance-foreigner/")({
  head: () => {
    const url = `${SITE}/police-clearance-foreigner`;
    const title = "ขอใบตรวจประวัติอาชญากรสำหรับชาวต่างชาติในไทย (25+ สัญชาติ) | NYC";
    const desc = "บริการครบวงจรขอ Police Clearance ประเทศบ้านเกิด สำหรับชาวต่างชาติที่พำนักในไทย พร้อม Apostille + แปลไทยรับรอง MFA · 25+ สัญชาติ — แจ้งเอกสารที่มี รู้ขั้นตอนและระยะเวลาใน 10 นาที";
    return {
      meta: [
        { title: ctaTitle(title, "/police-clearance-foreigner", "th") },
        { name: "description", content: ctaDesc(desc, "/police-clearance-foreigner", "th")},
        { property: "og:title", content: ctaTitle(title, "/police-clearance-foreigner", "th")},
        { property: "og:description", content: ctaDesc(desc, "/police-clearance-foreigner", "th")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "th_TH" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/police-clearance-foreigner", "/en/police-clearance-foreigner")],
      scripts: [
        consolidateGraph([
          speakableSchema(),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: "ขอใบตรวจประวัติอาชญากรสำหรับชาวต่างชาติในไทย",
              serviceType: "Foreign Police Clearance Procurement (in Thailand)",
              areaServed: { "@type": "Country", name: "Thailand" },
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", telephone: "+66832494999", url: SITE },
              offers: { "@type": "Offer", price: 5500, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
        ]),
      ],
    };
  },
  component: HubPage,
});

function HubPage() {
  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/police-clearance" className="hover:text-gold">Police Clearance</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">สำหรับชาวต่างชาติในไทย</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ShieldCheck className="h-3.5 w-3.5" /> Foreign Police Clearance · in Thailand
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            ใบตรวจประวัติอาชญากรจากประเทศบ้านเกิด สำหรับชาวต่างชาติในไทย
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            บริการครบวงจร — ขอ FBI/ACRO/AFP/RCMP/Führungszeugnis และเอกสารเทียบเท่ากว่า 25 สัญชาติ พร้อม Apostille/Embassy Legalization + แปลไทยรับรอง MFA — ใช้ต่อวีซ่า ตม. Non-B, Work Permit, สมรส, PR ไทย
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          NYC ประสานทำ Police Clearance จากประเทศบ้านเกิดของชาวต่างชาติที่พำนักในไทย ครอบคลุม {FOREIGNER_NATIONALITIES.length}+ สัญชาติ — ทุกขั้นตอน (ขอ PC → Apostille/Legalization ประเทศต้นทาง → แปลไทยรับรอง MFA) เริ่ม ฿5,500 เสร็จ 18–45 วัน โดยทนายที่ขึ้นทะเบียนสภาทนายความ
        </blockquote>

        <section className="mt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <Globe2 className="mr-2 inline-block h-6 w-6 text-amber-600" />
            เลือกสัญชาติของคุณ ({FOREIGNER_NATIONALITIES.length} ประเทศ)
          </h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {FOREIGNER_NATIONALITIES.map((n) => (
              <Link
                key={n.slug}
                to="/police-clearance-foreigner/$nationality"
                params={{ nationality: n.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 hover:border-gold/50 hover:shadow-md transition"
              >
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 shrink-0 text-amber-600" />
                  <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                    {n.nameTh} ({n.countryTh})
                  </div>
                </div>
                <div className="mt-1.5 text-[12px] text-slate-600 line-clamp-2">{n.homeAuthority}</div>
                <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                  <span>เริ่ม ฿{n.priceFromTh.toLocaleString()}</span>
                  <span>{n.timelineDays}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ทำไมต้องใช้ NYC</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong>ประสานงานทุกภาษา</strong> — ทีมงานพูดอังกฤษ/จีน/ญี่ปุ่น/รัสเซีย ช่วยประสาน authority บ้านเกิดโดยตรง</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong>Apostille/Legalization ครบวงจร</strong> — ประสานทั้ง Hague และ non-Hague chain (Notary → State → Embassy)</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong>แปลไทยรับรอง MFA</strong> — โดยนักแปลขึ้นทะเบียนกรมการกงสุล ใช้ยื่นตม. + กรมพัฒนาธุรกิจได้ทันที</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong>ทนายสภาทนายความ</strong> — เซ็นรับรองสำเนา/แปลได้ตาม พ.ร.บ.ทนายความ ให้ผลทางกฎหมายเต็ม</li>
          </ul>
        </section>

        <section className="mt-14"><ContactCTA /></section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-5">
            <FaqAccordion
              items={[
                { q: "ต้องไปประเทศบ้านเกิดเพื่อขอ Police Clearance หรือไม่?", a: "ไม่จำเป็นในหลายประเทศ — NYC ประสานผ่าน Online Portal (UK ACRO, US FBI Channeler, AU AFP, NZ MoJ, DE Konsulat) หรือมอบอำนาจให้ญาติ/สถานทูตในไทยดำเนินการ" },
                { q: "ต้องใช้เอกสารอะไรบ้าง?", a: "Passport + ที่อยู่ปัจจุบันในไทย + Fingerprint (บางประเทศ) + Certificate of Residence จาก ตม. (ถ้าจำเป็น)" },
                { q: "ใบ PC จากบ้านเกิดใช้ยื่นตม.ไทยได้เลยไหม?", a: "ต้องผ่าน Apostille หรือ Embassy Legalization จากประเทศต้นทาง + แปลไทยรับรอง MFA จึงใช้กับหน่วยงานราชการไทยได้" },
                { q: "เร็วสุดกี่วัน?", a: "บริการมาตรฐาน 5-7 วันทำการ ส่วนบริการเร่งด่วนแบบครบชุด (แปลรับรอง + MFA + Consular Legalization ที่สถานทูตปลายทาง) เสร็จภายใน 48-72 ชั่วโมง เหมาะสำหรับ deadline วีซ่าหรือการยื่นตรวจคนเข้าเมืองเร่งด่วน" },
                { q: "ราคาเริ่มต้นเท่าไร?", a: "แปลรับรอง Police Clearance เริ่มต้น ฿1,500 ต่อชุด, MFA Legalization +฿1,500 ต่อตราประทับ และ Consular Legalization ที่สถานทูตปลายทางแล้วแต่ประเทศ (฿2,500-฿6,500) ทีมงานประเมินราคารวมให้ก่อนยืนยันงาน ไม่มีค่าใช้จ่ายซ่อน" },
                { q: "รองรับกี่สัญชาติ?", a: `รองรับ ${FOREIGNER_NATIONALITIES.length}+ สัญชาติหลัก — สัญชาติอื่นๆ (แอฟริกา/ตะวันออกกลาง/ละตินอเมริกา) สอบถามเพิ่มเติมได้ครับ` },
              ]}
            />
          </div>
        </section>
      </div>

      <TrustLayer variant="compact" />
      <QuotableStatsStrip cluster="police" locale="th" variant="light" />
          <SiteCoreLinks />
    </main>
  );
}
